// A working day's arrival and departure, saved from the phone even with no signal (Session BDM).
//
// The engineer taps "I've arrived" / "I've left" on a multi-day job's panel. The save goes straight to the server when it can; when it
// cannot (no signal, a server that is down) the entry waits in this small queue on the device and is sent again at the next sync
// (reconnect, app brought to the front, the Sync now control). Nothing about the panel is a gate, so a missing signal never stops
// anyone working; it only delays the record.
//
// RULES, each pinned by workDayTimesQueue.test.ts:
//   - ONE ENTRY PER (job, day): a second save of the same day replaces the first (the later times win), so the queue never grows by
//     tapping, and what is sent is what the engineer last said.
//   - A network failure or a 5xx keeps the entry and tries again later; a 2xx removes it.
//   - A refusal that can never succeed (400, 403, 404, 409, 422) is NOT retried for ever: it is moved to `refused` with the server's
//     plain-English message, shown to the engineer, and removed when they dismiss it. The record is the engineer's own words, so it is
//     not silently dropped either: they see it and the office is told to ring them.
//   - THE REFUSED ENTRY KEEPS WHAT THE ENGINEER TYPED (Session R4F, review M3): the payload travels with it, so the times are on the
//     phone until the engineer dismisses them, can be sent again (the office may have put them back on the day) or copied for the
//     office. One refused entry per (job, day): a later refusal of the same day replaces the earlier one.
//   - Browser storage can be unavailable (a private window, cleared site data): every read and write is wrapped, and the panel works
//     without it (it just cannot queue).
import { storageKeySuffix } from "./phonePreview";

// Inside the Phone view's frame (Session BDZ) the queue is kept under its own key, so a save made while reviewing is never
// sent by the real app later. Outside the frame the suffix is empty and the keys are what they always were.
const KEY = `ele.workDayTimes.v1${storageKeySuffix()}`;
const REFUSED_KEY = `ele.workDayTimes.refused.v1${storageKeySuffix()}`;

export interface WorkDayTimePayload {
  arrivedAt?: string | null;
  leftAt?: string | null;
  note?: string | null;
  overtimeBooked?: boolean;
  lateReason?: string;
  lateReasonText?: string;
  /** When the engineer pressed save (set when the save had to wait in the queue), so the server judges the day they pressed it. */
  savedAt?: string;
}

export interface QueuedWorkDayTime {
  jobId: string;
  day: string;
  payload: WorkDayTimePayload;
  queuedAt: string;
  attempts: number;
}
export interface RefusedWorkDayTime {
  jobId: string;
  day: string;
  message: string;
  at: string;
  /** What the engineer saved, kept until they dismiss it (Session R4F). Absent on an entry an older build refused. */
  payload?: WorkDayTimePayload;
}

export type WorkDaySendResult = { ok: true } | { ok: false; refused: true; message: string } | { ok: false; refused: false };

function read<T>(key: string): T[] {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}
function write<T>(key: string, value: T[]): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable: the panel still works online.
  }
}

export function queuedWorkDayTimes(): QueuedWorkDayTime[] {
  return read<QueuedWorkDayTime>(KEY);
}
export function refusedWorkDayTimes(): RefusedWorkDayTime[] {
  return read<RefusedWorkDayTime>(REFUSED_KEY);
}

/** Queue (or replace) the day's entry. The later save of a day wins, field by field. */
export function enqueueWorkDayTime(jobId: string, day: string, payload: WorkDayTimePayload, now: Date = new Date()): void {
  const all = queuedWorkDayTimes();
  const i = all.findIndex((q) => q.jobId === jobId && q.day === day);
  if (i >= 0) all[i] = { ...all[i]!, payload: { ...all[i]!.payload, ...payload }, queuedAt: now.toISOString() };
  else all.push({ jobId, day, payload, queuedAt: now.toISOString(), attempts: 0 });
  write(KEY, all);
}

/** What is waiting for this day, if anything. */
export function queuedWorkDayTimeFor(jobId: string, day: string): QueuedWorkDayTime | undefined {
  return queuedWorkDayTimes().find((q) => q.jobId === jobId && q.day === day);
}
/** Forget the day's waiting entry (the server now has it, or has a newer word that includes it). */
export function dropQueuedWorkDayTime(jobId: string, day: string): void {
  write(
    KEY,
    queuedWorkDayTimes().filter((q) => !(q.jobId === jobId && q.day === day)),
  );
}

/** Keep a refused save on the phone, with what the engineer typed. Replaces an earlier refusal of the same day. */
export function recordRefusedWorkDayTime(entry: RefusedWorkDayTime): void {
  write(REFUSED_KEY, [...refusedWorkDayTimes().filter((r) => !(r.jobId === entry.jobId && r.day === entry.day)), entry]);
}

export function dismissRefusedWorkDayTime(jobId: string, day: string): void {
  write(
    REFUSED_KEY,
    refusedWorkDayTimes().filter((r) => !(r.jobId === jobId && r.day === day)),
  );
}

/** Send what is waiting. `send` is the API call; it reports whether the server took it, refused it for good, or could not be reached. */
let flushing = false;
export async function flushWorkDayTimes(send: (q: QueuedWorkDayTime) => Promise<WorkDaySendResult>, now: Date = new Date()): Promise<{ sent: number; refused: number; kept: number } | null> {
  if (flushing) return null;
  flushing = true;
  try {
    const items = queuedWorkDayTimes();
    const keep: QueuedWorkDayTime[] = [];
    const refused = refusedWorkDayTimes();
    let sent = 0;
    let refusedCount = 0;
    for (const item of items) {
      let result: WorkDaySendResult;
      try {
        result = await send(item);
      } catch {
        result = { ok: false, refused: false };
      }
      if (result.ok) sent += 1;
      else if (result.refused) {
        refusedCount += 1;
        // Session R4F (review M3): the payload stays with the refusal, so the engineer's times are never dropped with it.
        const entry = { jobId: item.jobId, day: item.day, message: result.message, at: now.toISOString(), payload: item.payload };
        const at = refused.findIndex((r) => r.jobId === item.jobId && r.day === item.day);
        if (at >= 0) refused[at] = entry;
        else refused.push(entry);
      } else keep.push({ ...item, attempts: item.attempts + 1 });
    }
    // A save made while this flush ran must not be lost: merge what the queue holds NOW with what we kept.
    // A save for a day this flush already handled counts when it is NEWER than the entry that was sent (a same-day save made mid-flush
    // was queued because its direct send failed): it replaces a kept copy of the older one rather than being dropped.
    const current = queuedWorkDayTimes();
    const sentAt = new Map(items.map((i) => [`${i.jobId}|${i.day}`, i.queuedAt]));
    const keyOf = (q: { jobId: string; day: string }) => `${q.jobId}|${q.day}`;
    const added = current.filter((c) => !sentAt.has(keyOf(c)) || c.queuedAt > sentAt.get(keyOf(c))!);
    const addedKeys = new Set(added.map(keyOf));
    write(KEY, [...keep.filter((k) => !addedKeys.has(keyOf(k))), ...added]);
    write(REFUSED_KEY, refused);
    return { sent, refused: refusedCount, kept: keep.length };
  } finally {
    flushing = false;
  }
}

/** Maps an API failure to the queue's three outcomes. 4xx other than 408/429 can never succeed on a retry. */
export function classifyWorkDaySend(status: number | null, message: string): WorkDaySendResult {
  if (status == null) return { ok: false, refused: false };
  if (status === 408 || status === 429 || status >= 500) return { ok: false, refused: false };
  if (status === 401) return { ok: false, refused: false }; // signed out: the sign-in layer decides; the entry waits
  if (status >= 400) return { ok: false, refused: true, message };
  return { ok: true };
}
